import styled from '@emotion/styled';
import React, { FC } from 'react';
import { Box } from '~Styles';
import { BlurryBox } from '~Components/LinkUser/styles';
import { useStorage } from '~Components/Storage';

/** Mirrors OrderRowContainer so the skeleton lines up with the real rows once loaded */
const SkeletonRow = styled.div<{ small?: boolean }>`
  display: flex;
  justify-content: space-between;
  background-color: var(--colorBlueGray1100);
  border-radius: 8px;
  padding: ${({ small }) => (small ? '8px 16px' : '16px')};
  margin-bottom: 8px;
  min-height: ${({ small }) => (small ? '44px' : '64px')};
  box-sizing: border-box;
  align-items: center;
  position: relative;
  overflow: hidden;
  border: 1px solid var(--colorBlueGray1100);
`;

/** Matches the left status bar (OrderColor) of a real OrderRow */
const SkeletonColor = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 4px;
  background-color: var(--colorBlueGray1300);
`;

/** Matches the fixed-width Column in OrderRow */
const SkeletonColumn = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 96px;
  min-width: 96px;
  flex-shrink: 0;
`;

/** A single column placeholder: a small label above a value */
const SkeletonColumnContent: FC = () => (
  <SkeletonColumn>
    <BlurryBox width="40px" height="12px" />
    <Box marginTop="xs">
      <BlurryBox width="56px" height="16px" />
    </Box>
  </SkeletonColumn>
);

interface Props {
  /** How many placeholder rows to render (defaults: 6 full / 2 small) */
  rows?: number;
  /**
   * Compact variant used when fetching more rows on scroll:
   * fewer/shorter rows and no date label so it reads as "loading more".
   */
  small?: boolean;
}

/**
 * Loading skeleton for the history order list.
 * Replicates the OrderRow layout (left status bar, fixed columns, trailing arrow)
 * grouped under a date label, so the placeholder matches the loaded rows.
 */
const OrderRowsSkeleton: FC<Props> = ({ rows, small = false }) => {
  const [storage] = useStorage();
  /** Tip column only renders on wide screens with tipping on, same as OrderRow */
  const showTipColumn = storage.enableTipping && window.innerWidth > 1380;

  /** Default to a fuller skeleton for the initial load, a tiny one when loading more */
  const rowCount = rows ?? (small ? 2 : 6);

  return (
    <Box marginBottom="medium" fluid>
      {/* Date group label placeholder (only for the full initial-load skeleton) */}
      {!small && (
        <Box marginBottom="xs">
          <BlurryBox width="90px" height="16px" />
        </Box>
      )}

      {Array.from({ length: rowCount }).map((_, index) => (
        <SkeletonRow key={index} small={small}>
          <SkeletonColor />

          <SkeletonColumnContent />
          <SkeletonColumnContent />
          <SkeletonColumnContent />
          {showTipColumn && <SkeletonColumnContent />}

          <Box display="flex" alignItems="center">
            <BlurryBox width="24px" height="24px" />
          </Box>
        </SkeletonRow>
      ))}
    </Box>
  );
};

export default OrderRowsSkeleton;
